import { useEffect, useRef, useState, useCallback } from "react";
import useWebSocket from "react-use-websocket";
import { WEBSOCKET_URL } from "../../store/config.js";
import { useAuth } from "../../store/auth/hooks";
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ContentTitleBar } from "../../components/ContentTitleBar";
import {
  AlignClipsSnackbarDL,
  AlignClipsSnackbarWS,
} from "../../components/snackbars";
import { DAWTimeDisplay } from "../../components/audiodaw/DAWTimeDisplay.js";
import { DAWControls } from "../../components/audiodaw/DAWControls.js";
import { getNavigationUrl } from "../../store/navigationUtil";
import {
  loadChannels,
  loadClip,
  loadClips,
  queueAlignmentRequest,
} from "./utils/dataLoader";
import { useAudioDAWState } from "../../components/audiodaw/react-audio-daw";
import { useSnackbars } from "../../store/snackbars/hooks";
import { AF_THEME_LIGHT } from "../../components/audiodaw/af-theme.js";
import { editorLoadGUID } from "./utils/dataLoader";
import { SERVER_URL } from "../../store/config.js";
import { SyncAltOutlined } from "@mui/icons-material";
import { NO_FILE_SELECTED_STATUSES } from "./utils/clipStatus.js";

const faderThumbStyle = {
  "& .MuiSlider-thumb": {
    height: 22,
    width: 8,
    borderRadius: "2px",
    backgroundColor: "#fff",
    border: "1px solid currentColor",
    marginLeft: "-4px", // Half of width
    transition:
      "box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, left 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, bottom 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    "&:hover, &.Mui-focusVisible": {
      boxShadow: "0px 0px 0px 6px rgba(156, 39, 176, 0.1)",
    },
    "&.Mui-active": {
      boxShadow: "0px 0px 0px 8px rgba(156, 39, 176, 0.125)",
    },
  },
  "& .MuiSlider-track": {
    height: 2,
  },
  "& .MuiSlider-rail": {
    height: 2,
  },
};

const gainLabelFormat = (value) => (value > 0 ? `+${value}dB` : `${value}dB`);

export default function AlignPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { openAlignClipsSnackbarDL, openAlignClipsSnackbarWS } = useSnackbars();
  const { caseId, clipId } = useParams();
  const [clip, setClip] = useState(null);
  const [primaryChannels, setPrimaryChannels] = useState([]);
  const [primaryTrackId, setPrimaryTrackId] = useState(null);
  const [selectedPrimaryChannelId, setSelectedPrimaryChannelId] = useState(1);
  const [secondaryClips, setSecondaryClips] = useState([]);
  const [selectedSecondaryClipId, setSelectedSecondaryClipId] = useState(null);
  const [secondaryChannels, setSecondaryChannels] = useState([]);
  const [selectedSecondaryChannelId, setSelectedSecondaryChannelId] =
    useState(null);
  const [secondaryTrackId, setSecondaryTrackId] = useState(null);
  const [loadingPrimary, setLoadingPrimary] = useState(false);
  const [loadingSecondary, setLoadingSecondary] = useState(false);
  const [trackUrl, setTrackUrl] = useState(null);
  const [secondaryTrackUrl, setSecondaryTrackUrl] = useState(null);
  const canvasRef = useRef();
  const minimapRef = useRef();
  const [primaryGainValue, setPrimaryGainValue] = useState(0);
  const [secondaryGainValue, setSecondaryGainValue] = useState(0);

  const { accessCode } = useAuth();
  const [isAligning, setIsAligning] = useState(false);
  const [isAligned, setIsAligned] = useState(null); // null = no secondary, true = aligned, false = needs alignment
  const [websocketRegistered, setWebsocketRegistered] = useState(false);
  const pendingRequestIdRef = useRef(null);
  const pendingAlignmentOffsetRef = useRef(null);
  const applyingAlignmentRef = useRef(false);
  const [primaryLoaded, setPrimaryLoaded] = useState(false);

  const { daw, isPlaying, currentTime, selection, zoom } = useAudioDAWState(
    canvasRef,
    {
      minimapCanvasRef: minimapRef,
      pixelsPerSecond: 100,
      followPlayhead: true,
      onTrackAdded: (id) => console.log("Track added:", id),
      onTrackOffsetChange: () => {
        if (!applyingAlignmentRef.current) {
          setIsAligned((prev) => (prev === null ? null : false));
        }
      },
      theme: AF_THEME_LIGHT,
    },
  );

  const applyAlignmentOffset = useCallback(
    (sampleOffset, primaryId, secondaryId) => {
      console.log(
        `Applying alignment offset: ${sampleOffset} samples (Primary ID: ${primaryId}, Secondary ID: ${secondaryId})`,
      );
      const tracks = daw?.getTracks();
      const primaryTrack = tracks?.find((t) => t.id === primaryId);
      const secondaryTrack = tracks?.find((t) => t.id === secondaryId);
      if (primaryTrack && secondaryTrack) {
        const sampleRate =
          primaryTrack.originalSampleRate ||
          primaryTrack.audioBuffer?.sampleRate ||
          44100;
        let primaryNewTime = 0;
        let secondaryNewTime = sampleOffset / sampleRate;
        if (secondaryNewTime < 0) {
          primaryNewTime = -secondaryNewTime;
          secondaryNewTime = 0;
        }
        applyingAlignmentRef.current = true;
        daw.setTrackOffset(primaryId, primaryNewTime);
        daw.setTrackOffset(secondaryId, secondaryNewTime);
        applyingAlignmentRef.current = false;
      }
    },
    [daw],
  );

  const handlePrimaryGainChange = (_, newValue) => {
    daw.setTrackGain(primaryTrackId, newValue);
    setPrimaryGainValue(newValue);
  };

  const handleSecondaryGainChange = (_, newValue) => {
    daw.setTrackGain(secondaryTrackId, newValue);
    setSecondaryGainValue(newValue);
  };

  const load = useCallback(async () => {
    try {
      const currClip = await loadClip(caseId, clipId);
      if (!currClip) {
        navigate(getNavigationUrl(caseId));
        return;
      }
      setClip(currClip);
      setLoadingPrimary(true);
      const guid = await editorLoadGUID(currClip, selectedPrimaryChannelId);
      setTrackUrl(SERVER_URL + "/api/player/audio/" + guid);
      const primChans = await loadChannels(caseId, clipId);
      setPrimaryChannels(primChans);
      const allClips = await loadClips(caseId);
      // Filter out the primary clip from secondary options
      const secClips = allClips.filter(
        (c) =>
          c.id !== currClip.id &&
          !c.deleted &&
          !NO_FILE_SELECTED_STATUSES.includes(c?.status),
      );
      setSecondaryClips(secClips);
      setLoadingPrimary(false);
    } catch (error) {
      console.error("Error loading align page:", error);
      setLoadingPrimary(false);
    }
  }, [caseId, clipId, navigate, selectedPrimaryChannelId]);

  const setTrackAudio = useCallback(
    async (trackId, url) => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.statusText}`);
        }
        const blob = await response.blob();
        const file = new File([blob], "Clip 1", {
          type: blob.type || "audio/wav",
        });
        if (daw) {
          await daw.setTrackAudio(trackId, file);
          console.log(`Set audio for track ID: ${trackId}`);
          setPrimaryLoaded(true);
        }
      } catch (error) {
        console.error("Error setting track audio:", error);
      }
    },
    [daw],
  );

  const loadAudioFromUrl = useCallback(
    async (url, fileName) => {
      try {
        // Fetch the audio file
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.statusText}`);
        }

        // Get the audio data as a Blob first (better for audio files)
        const blob = await response.blob();

        // Convert Blob to File for better compatibility
        const file = new File([blob], fileName, {
          type: blob.type || "audio/wav",
        });

        // Add to DAW (DAW will handle converting File to ArrayBuffer internally)
        if (daw) {
          const trackId = await daw.addTrack(file, {
            color: daw.renderer.theme.defaultWaveformColor,
            fileName: fileName,
            volume: 0.8,
          });

          console.log(`Loaded track: ${fileName} (${trackId})`);
          return trackId;
        }
      } catch (error) {
        console.error("Error loading audio from URL:", error);

        // Provide more detailed error information
        if (error.name === "EncodingError") {
          console.error(
            "Audio decoding failed. The file may be corrupted or in an unsupported format.",
          );
          console.error(
            "Try converting to a standard format (16-bit PCM WAV or MP3)",
          );
        }

        throw error;
      }
    },
    [daw],
  );

  useEffect(() => {
    if (trackUrl && daw) {
      if (primaryTrackId) {
        setTrackAudio(primaryTrackId, trackUrl);
      } else {
        loadAudioFromUrl(trackUrl, "Clip 1").then((trackId) => {
          console.log("Clip one loaded with ID:", trackId);
          setPrimaryTrackId(trackId);
        });
      }
    }
  }, [trackUrl, daw, primaryTrackId, setTrackAudio, loadAudioFromUrl]);

  useEffect(() => {
    if (daw) {
      if (daw.timeline.regionGroups.size === 0) {
        const groupId = daw.createRegionGroup("Alignment", {
          maxRegions: 1,
        });
        daw.setActiveRegionGroup(groupId);
      } else if (!daw.activeRegionGroupId) {
        const firstGroupId = Array.from(daw.timeline.regionGroups.keys())[0];
        daw.setActiveRegionGroup(firstGroupId);
      }
    }
  }, [daw]);

  useEffect(() => {
    if (secondaryTrackUrl && daw) {
      if (secondaryTrackId) {
        setTrackAudio(secondaryTrackId, secondaryTrackUrl);
      } else {
        loadAudioFromUrl(secondaryTrackUrl, "Clip 2").then((trackId) => {
          console.log("Clip 2 loaded with ID:", trackId);
          setSecondaryTrackId(trackId);
        });
      }
    }
  }, [
    secondaryTrackUrl,
    daw,
    secondaryTrackId,
    setTrackAudio,
    loadAudioFromUrl,
  ]);

  const handlePrimaryChannelChange = async (e) => {
    const newChannelId = e.target.value;
    setSelectedPrimaryChannelId(newChannelId);
    setLoadingPrimary(true);
    try {
      const guid = await editorLoadGUID(clip, newChannelId);
      setTrackUrl(SERVER_URL + "/api/player/audio/" + guid);
      setLoadingPrimary(false);
    } catch (error) {
      console.error("Error loading primary channel:", error);
      setLoadingPrimary(false);
    }
  };

  const handleSecondaryClipChange = async (e) => {
    const newClipId = e.target.value;
    setSelectedSecondaryClipId(newClipId);
    setIsAligned(null);
    setLoadingSecondary(true);
    try {
      // Load channels for the selected secondary clip
      const secChans = await loadChannels(caseId, newClipId);
      setSecondaryChannels(secChans);
      // Default to first channel
      if (secChans.length > 0) {
        const defaultChannelId = 1;
        setSelectedSecondaryChannelId(defaultChannelId);
        // Load the secondary clip
        const secondaryClip = secondaryClips.find((c) => c.id === newClipId);
        if (secondaryClip) {
          const existingAlignment = clip.alignments?.find(
            (a) => a.alignedClipId === Number(newClipId),
          );
          const sampleOffset = existingAlignment?.sampleOffset ?? null;
          if (secondaryTrackId && primaryTrackId) {
            // DAW track already exists from a prior selection — reset then apply if aligned
            applyAlignmentOffset(
              sampleOffset ?? 0,
              primaryTrackId,
              secondaryTrackId,
            );
            if (existingAlignment) setIsAligned(true);
          } else {
            // First secondary load — stash for the useEffect to pick up after track loads
            pendingAlignmentOffsetRef.current = sampleOffset;
          }
          const guid = await editorLoadGUID(secondaryClip, defaultChannelId);
          setSecondaryTrackUrl(SERVER_URL + "/api/player/audio/" + guid);
        }
      }
      setLoadingSecondary(false);
    } catch (error) {
      console.error("Error loading secondary clip:", error);
      setLoadingSecondary(false);
    }
  };

  const handleSecondaryChannelChange = async (e) => {
    const newChannelId = e.target.value;
    setSelectedSecondaryChannelId(newChannelId);
    setLoadingSecondary(true);
    try {
      const secondaryClip = secondaryClips.find(
        (c) => c.id === selectedSecondaryClipId,
      );
      if (secondaryClip) {
        const guid = await editorLoadGUID(secondaryClip, newChannelId);
        setSecondaryTrackUrl(SERVER_URL + "/api/player/audio/" + guid);
      }
      setLoadingSecondary(false);
    } catch (error) {
      console.error("Error loading secondary channel:", error);
      setLoadingSecondary(false);
    }
  };

  useEffect(() => {
    if (
      secondaryTrackId &&
      primaryTrackId &&
      pendingAlignmentOffsetRef.current !== null
    ) {
      applyAlignmentOffset(
        pendingAlignmentOffsetRef.current,
        primaryTrackId,
        secondaryTrackId,
      );
      setIsAligned(true);
      pendingAlignmentOffsetRef.current = null;
    }
  }, [secondaryTrackId, primaryTrackId, applyAlignmentOffset]);

  const { sendMessage } = useWebSocket(WEBSOCKET_URL, {
    share: true,
    onOpen: () => {
      console.log("WebSocket connection established.", WEBSOCKET_URL);
    },
    onMessage: (event) => {
      if (!event?.data) return;
      const data = JSON.parse(event.data);
      console.log("[AlignPage][WebSocket] Received message:", data);

      if (
        (data.command === "ALIGNMENT_COMPLETE" ||
          data.command === "ALIGNMENT_FAILED") &&
        data.data.primaryClipId === Number(clipId) &&
        data.data.requestId === pendingRequestIdRef.current
      ) {
        if (data.command === "ALIGNMENT_FAILED") {
          openAlignClipsSnackbarWS({
            message: data.command,
            status: data.command,
          });
          console.log("[AlignPage][WebSocket] ALIGNMENT_FAILED", data.data);
          pendingRequestIdRef.current = null;
          setIsAligning(false);
        } else {
          openAlignClipsSnackbarWS({
            message: data.command,
            status: data.command,
          });
          console.log("[AlignPage][WebSocket] ALIGNMENT_COMPLETE", data.data);
          const { finalOffsetSamples } = data.data;
          applyAlignmentOffset(
            finalOffsetSamples,
            primaryTrackId,
            secondaryTrackId,
          );
          setIsAligned(true);
          pendingRequestIdRef.current = null;
          setIsAligning(false);
          loadClip(caseId, clipId).then((updatedClip) => {
            if (updatedClip) setClip(updatedClip);
          });
        }
      }
    },
  });

  useEffect(() => {
    if (!websocketRegistered) {
      setWebsocketRegistered(true);
      sendMessage(
        JSON.stringify({ command: "REGISTER", authorization: accessCode }),
      );
    }
  }, [accessCode, websocketRegistered, sendMessage]);

  const handleAlign = async () => {
    if (!selectedSecondaryClipId) {
      console.error("No secondary clip selected");
      return;
    }

    // Get track offsets from DAW
    const primaryOffset =
      daw?.getTracks()?.find((track) => track.id === primaryTrackId)
        ?.sampleOffset || 0;
    const secondaryOffset =
      daw?.getTracks()?.find((track) => track.id === secondaryTrackId)
        ?.sampleOffset || 0;

    const requestId = crypto.randomUUID();
    pendingRequestIdRef.current = requestId;

    const alignmentData = {
      sourceClipId: clipId,
      targetClipId: selectedSecondaryClipId,
      initialOffsetSamples: secondaryOffset - primaryOffset,
      region: selection[0]
        ? {
            startTimeSeconds: selection[0].start,
            endTimeSeconds: selection[0].end,
          }
        : null,
    };

    console.log("Alignment data:", alignmentData);

    setIsAligning(true);
    const response = await queueAlignmentRequest(
      caseId,
      clipId,
      alignmentData,
      requestId,
    );
    if (response.status === "FAILURE") {
      pendingRequestIdRef.current = null;
      console.log("Alignment request failed:", response);
      setIsAligning(false);
    }
    openAlignClipsSnackbarDL(response);
  };

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} p={2} spacing={1}>
        <DAWTimeDisplay
          duration={daw?.getDuration() || 0}
          currentTime={currentTime}
          mode={daw?.renderer?.theme?.mode}
        />
        <Paper
          sx={{
            background: theme.palette.grey[50],
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: 1,
          }}
          elevation={4}
        >
          <Grid
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 1,
              boxShadow: 1,
              p: 1,
            }}
            container
          >
            <Grid
              size={{ xs: 4 }}
              display="flex"
              gap={1}
              alignItems="center"
              maxWidth={300}
            >
              <Typography fontWeight={500} lineHeight={1} variant="overline">
                Clip 1:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "50%",
                }}
              >
                {clip?.description}
              </Typography>
            </Grid>
            <Grid
              size={{ xs: 4 }}
              display="flex"
              gap={1}
              alignItems="center"
              maxWidth={200}
            >
              <Typography fontWeight={500} lineHeight={1} variant="overline">
                Channel:
              </Typography>
              <FormControl fullWidth sx={{ width: 52 }} variant="standard">
                <Select
                  disabled={loadingPrimary || isAligning}
                  value={
                    Boolean(primaryChannels.length)
                      ? selectedPrimaryChannelId
                      : ""
                  }
                  onChange={handlePrimaryChannelChange}
                  displayEmpty
                  inputProps={{ "aria-label": "primary channel" }}
                  MenuProps={{
                    closeAfterTransition: true,
                  }}
                >
                  {primaryChannels.map((chan) => (
                    <MenuItem
                      key={chan.id}
                      value={primaryChannels.indexOf(chan) + 1}
                    >
                      {primaryChannels.indexOf(chan) + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{ xs: 4 }}
              display="flex"
              gap={1}
              alignItems="center"
              maxWidth={400}
            >
              <Typography fontWeight={500} lineHeight={1} variant="overline">
                Gain:
              </Typography>
              <Slider
                color="secondary"
                disabled={!primaryTrackId || loadingPrimary}
                size="small"
                defaultValue={0}
                aria-label="primary gain"
                value={primaryGainValue}
                min={-40}
                max={40}
                step={1}
                onChange={handlePrimaryGainChange}
                sx={faderThumbStyle}
              />
              <Typography
                fontWeight={500}
                lineHeight={1}
                sx={{ minWidth: 38, textAlign: "right" }}
                variant="caption"
              >
                {gainLabelFormat(primaryGainValue)}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 1,
              boxShadow: 1,
              p: 1,
            }}
            container
          >
            <Grid
              size={{ xs: 4 }}
              display="flex"
              gap={1}
              alignItems="center"
              maxWidth={300}
            >
              <Typography fontWeight={500} lineHeight={1} variant="overline">
                Clip 2:
              </Typography>
              <FormControl
                fullWidth
                sx={{ width: "fit-content", maxWidth: "50%" }}
                variant="standard"
              >
                <Select
                  disabled={loadingSecondary || isAligning}
                  value={selectedSecondaryClipId || ""}
                  onChange={handleSecondaryClipChange}
                  displayEmpty
                  inputProps={{ "aria-label": "secondary clip" }}
                  MenuProps={{
                    closeAfterTransition: true,
                  }}
                >
                  <MenuItem value="" disabled>
                    {primaryLoaded
                      ? secondaryClips.length === 0
                        ? "No alignable clips"
                        : "Select a clip"
                      : "Loading..."}
                  </MenuItem>
                  {secondaryClips.map((sClip) => (
                    <MenuItem key={sClip.id} value={sClip.id}>
                      {sClip.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {selectedSecondaryClipId && (
              <>
                <Grid
                  size={{ xs: 4 }}
                  display="flex"
                  gap={1}
                  alignItems="center"
                  maxWidth={200}
                >
                  <Typography
                    fontWeight={500}
                    lineHeight={1}
                    variant="overline"
                  >
                    Channel:
                  </Typography>
                  <FormControl fullWidth sx={{ width: 52 }} variant="standard">
                    <Select
                      value={selectedSecondaryChannelId || ""}
                      onChange={handleSecondaryChannelChange}
                      disabled={
                        loadingSecondary ||
                        isAligning ||
                        !selectedSecondaryClipId
                      }
                      displayEmpty
                      inputProps={{ "aria-label": "secondary channel" }}
                      MenuProps={{
                        closeAfterTransition: true,
                      }}
                    >
                      {secondaryChannels.map((chan) => (
                        <MenuItem
                          key={chan.id}
                          value={secondaryChannels.indexOf(chan) + 1}
                        >
                          {secondaryChannels.indexOf(chan) + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  size={{ xs: 4 }}
                  display="flex"
                  gap={1}
                  alignItems="center"
                  maxWidth={400}
                >
                  <Typography
                    fontWeight={500}
                    lineHeight={1}
                    variant="overline"
                  >
                    Gain:
                  </Typography>
                  <Slider
                    disabled={
                      !secondaryTrackId ||
                      loadingSecondary ||
                      isAligning ||
                      !selectedSecondaryClipId
                    }
                    color="secondary"
                    size="small"
                    defaultValue={50}
                    aria-label="secondary gain"
                    value={secondaryGainValue}
                    min={-40}
                    max={40}
                    step={1}
                    onChange={handleSecondaryGainChange}
                    sx={faderThumbStyle}
                  />
                  <Typography
                    fontWeight={500}
                    lineHeight={1}
                    sx={{ minWidth: 38, textAlign: "right" }}
                    variant="caption"
                  >
                    {gainLabelFormat(secondaryGainValue)}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="contained"
              onClick={handleAlign}
              disabled={
                !selectedSecondaryClipId ||
                loadingPrimary ||
                loadingSecondary ||
                isAligning ||
                selection.length !== 1
              }
              loading={loadingPrimary || loadingSecondary || isAligning}
              size="small"
            >
              Apply Alignment
            </Button>
            {isAligned !== null && (
              <Chip
                color={isAligned ? "success" : "default"}
                label={isAligned ? "ALIGNED" : "NOT ALIGNED"}
                icon={<SyncAltOutlined fontSize="small" />}
                variant={isAligned ? "filled" : "outlined"}
                sx={{
                  fontWeight: 500,
                  height: 30,
                  lineHeight: 1,
                  ml: 1,
                  px: 0.5,
                }}
              />
            )}
          </Box>
        </Paper>
        <Box display="flex" flexDirection="column" width={1}>
          <Box
            width="100%"
            height={50}
            sx={{
              borderBottom: "1px solid",
              borderColor: theme.palette.grey[700],
            }}
          >
            <canvas
              ref={minimapRef}
              style={{ width: "100%", height: "100%" }}
            />
          </Box>
          <Box
            // this will need to be adjusted based on additional elements
            height="calc(100vh - 466px)"
            width="100%"
          >
            <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
          </Box>
        </Box>
        <DAWControls
          daw={daw}
          isPlaying={isPlaying}
          currentTime={currentTime}
          zoom={zoom}
        />
      </Stack>
      <ContentTitleBar
        name={clip?.description}
        label={clip?.afCase?.caseName}
        onBack={() => navigate(`/app/cases/${caseId}/clips`)}
      />
      <AlignClipsSnackbarDL />
      <AlignClipsSnackbarWS />
    </Stack>
  );
}
